import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';

/**
 * Class that creates a div called liveElement with aria-live and adds it to the body
 * for announcements or notifications to screen reader.
 * It would handle one announcement at a time.
 * It could be used when the screen reader has no feedback once an action is completed
 * for example when a pill filter is removed by clicking the close button.
 */

@Injectable({
    providedIn: 'root',
})
export class LiveAnnouncerService implements OnDestroy {
    private readonly elementClass = 'finder-live-announcer';
    private liveElement: HTMLElement;

    constructor(@Inject(DOCUMENT) private document: Document) {
        this.removeElements();
        this.liveElement = this.createElement();
        this.document.body.appendChild(this.liveElement);
    }

    ngOnDestroy() {
        this.removeElements();
    }

    /**
     * Assigns textContent to the liveElement.
     *
     * @param message the message to be announced by screen reader.
     */
    announce(message: string): void {
        this.liveElement.textContent = message;
    }

    /**
     * Removes all elements created with class 'finder-live-announcer'.
     */
    private removeElements(): void {
        const liveElements = this.document.querySelectorAll(`.${this.elementClass}`);

        if (liveElements.length <= 0) {
            return;
        }

        liveElements.forEach(element => element.remove());
    }

    /**
     * Creates a div with aria-live inside the body.
     * The div has the class 'finder-live-announcer' as an identifier
     * and the global class 'visually-hidden' to hide it visually.
     */
    private createElement(): HTMLElement {
        const element = this.document.createElement('div');
        element.classList.add(this.elementClass, 'visually-hidden');
        element.setAttribute('aria-live', 'polite');
        element.setAttribute('aria-atomic', 'true');

        return element;
    }
}
